import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useRef } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  searchUnaffiliatedUsersApi,
  addExistingUserApi,
  type UnaffiliatedUser,
} from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { Loader2, MailIcon, TagIcon, UserPlusIcon, UsersIcon } from 'lucide-react'

export const Route = createFileRoute('/dashboard/add-existing-user')({
  component: () => {
    const [search, setSearch] = useState('')
    const [searchVersion, setSearchVersion] = useState(0)
    const [results, setResults] = useState<UnaffiliatedUser[]>([])
    const [loading, setLoading] = useState(false)
    const [adding, setAdding] = useState<Set<number>>(new Set())
    const [hasSearched, setHasSearched] = useState(false)
    const searchRef = useRef('')

    useEffect(() => {
      searchRef.current = search
    }, [search])

    // Debounce search
    const mountedRef = useRef(false)
    useEffect(() => {
      if (!mountedRef.current) {
        mountedRef.current = true
        return
      }
      const timer = setTimeout(() => {
        setSearchVersion((v) => v + 1)
      }, 400)
      return () => clearTimeout(timer)
    }, [search])

    // Fetch results
    useEffect(() => {
      if (searchVersion === 0) return
      const q = searchRef.current.trim()
      if (q.length < 2) {
        setResults([])
        setHasSearched(false)
        return
      }

      setLoading(true)
      setHasSearched(true)
      searchUnaffiliatedUsersApi(q)
        .then((res) => setResults(res.data))
        .catch(() => toast.error('Failed to search users'))
        .finally(() => setLoading(false))
    }, [searchVersion])

    const handleAddUser = async (user: UnaffiliatedUser) => {
      setAdding((prev) => {
        const next = new Set(prev)
        next.add(user.user_id)
        return next
      })
      try {
        await addExistingUserApi(user.user_id)
        toast.success(`${user.name} has been added to the company`)
        // Remove from results
        setResults((prev) => prev.filter((u) => u.user_id !== user.user_id))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to add user')
      } finally {
        setAdding((prev) => {
          const next = new Set(prev)
          next.delete(user.user_id)
          return next
        })
      }
    }

    return (
      <div className="mx-auto max-w-2xl">
        {/* Page header with consistent branding */}
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">Add existing user</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Search for unaffiliated users by email or pseudonym and add them to your company.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userSearch">Search users</Label>
            <Input
              id="userSearch"
              type="text"
              placeholder="Search by email or pseudonym (min. 2 characters)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Only users not yet associated with any company will appear in the results.
            </p>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-8 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Searching…
            </div>
          )}

          {/* Results */}
          {!loading && results.length > 0 && (
            <div className="space-y-2">
              {results.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-muted/30"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10 text-xs font-semibold text-[var(--brand-mint)]">
                      {user.first_name?.charAt(0)}
                      {user.last_name?.charAt(0)}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium truncate">{user.name}</span>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <MailIcon className="size-3.5" />
                          {user.email}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <TagIcon className="size-3.5" />
                          {user.pseudonym}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => handleAddUser(user)}
                    disabled={adding.has(user.user_id)}
                  >
                    {adding.has(user.user_id) ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Adding…
                      </>
                    ) : (
                      <>
                        <UserPlusIcon className="size-4" />
                        Add
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && hasSearched && results.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-16">
              <UsersIcon className="size-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No unaffiliated users found matching "{searchRef.current}".
              </p>
              <p className="text-xs text-muted-foreground">
                Try a different email or pseudonym.
              </p>
            </div>
          )}

          {/* Initial state (no search yet) */}
          {!loading && !hasSearched && (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-16">
              <UsersIcon className="size-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Enter an email or pseudonym to search for users who are not yet part of any company.
              </p>
            </div>
          )}
        </div>
      </div>
    )
  },
})
